// src/services/ApiService.ts
import axios from 'axios';
import WebSocketService from './WebSocketService';
import { WebSocketMessage } from './WebSocketService';

// API endpoints from environment variables
const HTTP_API_URL = process.env.REACT_APP_HTTP_API_URL || 'https://k5tlfv2y58.execute-api.us-east-1.amazonaws.com/prod';

// Device interface
export interface Device {
  id: string;
  deviceId?: string;
  name: string;
  deviceType: string;
  room: string;
  capabilities: string[];
  state: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  userId?: string;
  isVirtual?: boolean;
}

// Re-export WebSocketMessage interface
export type { WebSocketMessage };

class ApiService {
  private messageHandlers: ((message: WebSocketMessage) => void)[] = [];
  private pollingInterval: NodeJS.Timeout | null = null;
  private pollingDelay = 5000; // 5 seconds
  private useWebSocket = true; // Set to true to use WebSocket, false to use polling

  /**
   * Get all devices
   * @param type Optional device type filter
   * @param room Optional room filter
   * @returns Promise resolving to an array of devices
   */
  async getDevices(type?: string, room?: string): Promise<Device[]> {
    try {
      let url = `${HTTP_API_URL}/devices`;
      
      // Add query parameters if provided
      const params = new URLSearchParams();
      if (type) params.append('type', type);
      if (room) params.append('room', room);
      
      const queryString = params.toString();
      if (queryString) url += `?${queryString}`;
      
      console.log('Fetching devices from:', url);
      const response = await axios.get(url);
      
      if (response.data && response.data.devices) {
        console.log(`Received ${response.data.devices.length} devices from API`);
        return response.data.devices;
      } else {
        console.log('API response format unexpected:', response.data);
        return response.data || [];
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
      return [];
    }
  }

  /**
   * Get a specific device by ID
   * @param deviceId The device ID
   * @returns Promise resolving to the device
   */
  async getDevice(deviceId: string): Promise<Device | null> {
    try {
      const response = await axios.get(`${HTTP_API_URL}/devices/${deviceId}`);
      return response.data.device || null;
    } catch (error) {
      console.error(`Error fetching device ${deviceId}:`, error);
      return null;
    }
  }

  /**
   * Register a new device
   * @param device The device data
   * @returns Promise resolving to the created device
   */
  async registerDevice(device: Partial<Device>): Promise<Device | null> {
    try {
      console.log('Registering device with data:', JSON.stringify(device));
      
      // Ensure both id and deviceId are set if either is provided
      if (device.id && !device.deviceId) {
        device.deviceId = device.id;
      } else if (device.deviceId && !device.id) {
        device.id = device.deviceId;
      }
      
      // Set isVirtual flag if the device ID starts with 'virtual-'
      if ((device.id && device.id.startsWith('virtual-')) || 
          (device.deviceId && device.deviceId.startsWith('virtual-'))) {
        device.isVirtual = true;
      }
      
      const response = await axios.post(`${HTTP_API_URL}/devices`, device, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('API response:', response.data);
      
      // Notify handlers of the new device
      if (response.data && response.data.id) {
        this.messageHandlers.forEach(handler => handler({
          event: 'deviceCreated',
          deviceId: response.data.id,
          state: response.data.state || {},
          timestamp: new Date().toISOString()
        }));
      }
      
      // Force a refresh of all devices after creating a new one
      setTimeout(async () => {
        try {
          console.log('Refreshing devices after creation');
          await this.getDevices();
        } catch (refreshError) {
          console.error('Error refreshing devices:', refreshError);
        }
      }, 1000);
      
      return response.data || null;
    } catch (error) {
      console.error('Error registering device:', error);
      
      // Try to extract error message from the response if available
      let errorMessage = 'Unknown error';
      if (axios.isAxiosError(error) && error.response) {
        errorMessage = error.response.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      throw new Error(`Failed to register device: ${errorMessage}`);
    }
  }

  /**
   * Control a device by updating its state
   * @param deviceId The device ID
   * @param state The new device state
   * @returns Promise resolving to the updated state
   */
  async controlDevice(deviceId: string, state: Record<string, any>): Promise<Record<string, any> | null> {
    try {
      console.log(`Sending control request for device ${deviceId} with state:`, state);
      
      const response = await axios.post(`${HTTP_API_URL}/devices/${deviceId}/control`, { state }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log(`Control response for device ${deviceId}:`, response.data);
      
      // Notify handlers of the update immediately for better responsiveness
      if (response.data && response.data.state) {
        this.messageHandlers.forEach(handler => handler({
          event: 'deviceUpdate',
          deviceId: deviceId,
          state: response.data.state,
          timestamp: new Date().toISOString()
        }));
      }
      
      return response.data.state || null;
    } catch (error) {
      console.error(`Error controlling device ${deviceId}:`, error);
      
      // Try to extract error message from the response if available
      let errorMessage = 'Unknown error';
      if (axios.isAxiosError(error) && error.response) {
        errorMessage = error.response.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      // Throw a more informative error
      throw new Error(`Failed to control device ${deviceId}: ${errorMessage}`);
    }
  }

  /**
   * Send a command to the API
   * @param text The command text
   * @returns Promise resolving to the response
   */
  async sendCommand(text: string): Promise<any> {
    try {
      // Try the command endpoint first
      try {
        const response = await axios.post(`${HTTP_API_URL}/command`, { text }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        return response.data || {};
      } catch (commandError) {
        // Fall back to input endpoint if command fails
        console.warn('Command endpoint failed, trying input endpoint:', commandError);
        const response = await axios.post(`${HTTP_API_URL}/input`, { text }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        return response.data || {};
      }
    } catch (error) {
      console.error('Error sending command:', error);
      throw error;
    }
  }

  /**
   * Start polling for device updates
   * @param deviceIds Array of device IDs to poll for
   */
  startPolling(deviceIds: string[]): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }

    this.pollingInterval = setInterval(async () => {
      try {
        for (const deviceId of deviceIds) {
          const device = await this.getDevice(deviceId);
          
          if (device) {
            // Notify handlers of the update
            this.messageHandlers.forEach(handler => handler({
              event: 'deviceUpdate',
              deviceId: device.id,
              state: device.state,
              timestamp: new Date().toISOString()
            }));
          }
        }
      } catch (error) {
        console.error('Error polling for device updates:', error);
      }
    }, this.pollingDelay);
  }

  /**
   * Stop polling for device updates
   */
  stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Subscribe to updates for a specific device
   * @param deviceId The device ID
   */
  async subscribeToDevice(deviceId: string): Promise<void> {
    if (this.useWebSocket) {
      await WebSocketService.subscribeToDevice(deviceId);
    } else {
      // Fall back to polling if WebSocket is disabled
      this.startPolling([deviceId]);
    }
  }

  /**
   * Unsubscribe from updates for a specific device
   * @param deviceId The device ID
   */
  async unsubscribeFromDevice(deviceId: string): Promise<void> {
    if (this.useWebSocket) {
      await WebSocketService.unsubscribeFromDevice(deviceId);
    } else {
      // Stop polling if WebSocket is disabled
      this.stopPolling();
    }
  }

  /**
   * Add a handler for device update messages
   * @param handler The message handler function
   */
  addMessageHandler(handler: (message: WebSocketMessage) => void): void {
    this.messageHandlers.push(handler);
    
    if (this.useWebSocket) {
      WebSocketService.addMessageHandler(handler);
    }
  }

  /**
   * Remove a message handler
   * @param handler The handler to remove
   */
  removeMessageHandler(handler: (message: WebSocketMessage) => void): void {
    this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
    
    if (this.useWebSocket) {
      WebSocketService.removeMessageHandler(handler);
    }
  }

  /**
   * Clean up resources
   */
  cleanup(): void {
    this.stopPolling();
    
    if (this.useWebSocket) {
      WebSocketService.disconnect();
    }
    
    this.messageHandlers = [];
  }
  
  /**
   * Connect to WebSocket for real-time updates
   * @returns Promise resolving when connected
   */
  async connectWebSocket(): Promise<void> {
    if (this.useWebSocket) {
      return WebSocketService.connect();
    } else {
      // Fall back to polling if WebSocket is disabled
      this.startPolling([]);
      return Promise.resolve();
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnectWebSocket(): void {
    if (this.useWebSocket) {
      WebSocketService.disconnect();
    } else {
      // Stop polling if WebSocket is disabled
      this.stopPolling();
    }
  }
}

export default new ApiService();